<template>
  <div class="layout-fullscreen layout-fullscreen--no-header">
    <HeaderBar :modifiers="['main']">
      <div class="header-bar__title typo-title">
        {{ $t('regions.manage_regions') }}
      </div>

      <template #right>
        <a
          class="button"
          @click="$router.back()"
        >
          {{ $t('general.cancel') }}
        </a>
        <FormSubmitButton
          :buttonLabel="$t('regions.save_region')"
          class="show-for-medium"
          :isSaving="saving"
          @click="save"
        />
      </template>
    </HeaderBar>

    <AlertBar v-if="errorMessage">
      {{ errorMessage }}
    </AlertBar>

    <div class="layout-panel hide-for-medium">
      <div class="layout-panel__panel">
        <EmptyMessage
          :message="$t('regions.unsupported_device_size_message')"
          :title="$t('regions.unsupported_device_size')"
        >
          <ExclamationOctagonSvg />
          <template #actions>
            <a
              class="button button--inverse"
              @click="$router.back()"
            >
              {{ $t('general.go_back') }}
            </a>
          </template>
        </EmptyMessage>
      </div>
    </div>

    <div class="layout-regions show-for-medium">
      <div class="layout-regions__regions">
        <p
          v-if="data?.name"
          class="current-item-name"
        >
          <strong>{{ data?.name }}</strong>
        </p>
        <RegionTree
          v-if="regionTree?.length > 0 && !loading"
          :allRegions="false"
          class="layout-regions__regions-tree"
          :modifiers="['manage-scroll']"
          :project="false"
          :regionTree="regionTree"
          :selectedRegion="selectedRegion"
          @select="selectRegion"
        />
        <EmptyMessage
          v-else-if="regionTree?.length <= 0"
          class="layout-regions__message"
          :title="$t('regions.no_regions')"
        />
        <Loader v-else />
      </div>

      <div class="layout-regions__content">
        <div :class="{ 'layout-regions__issues': true, 'layout-regions__issues--center': !selectedRegion }">
          <EmptyMessage
            v-if="!selectedRegion"
            :title="$t('regions.no_selection')"
          />
          <template v-if="selectedRegion">
            <div class="form scroll-container">
              <FormRow
                v-if="selectedRegion.errors || isValidField('bound')"
                :errors="selectedRegion.errors[validationData.operation][`${validationData.operation_regions}.bounds`]"
                :modifiers="['narrow']"
              />

              <div class="form__group form__group--margin-top">
                <FormRow
                  :errors="selectedRegion.errors?.name || isValidField('name')"
                  :label="$t('regions.region_name_label')"
                  labelFor="region_name"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <div
                      :class="{
                        input: true,
                        'input--error': selectedRegion.errors?.name || isValidField('name'),
                      }"
                    >
                      <input
                        id="region_name"
                        v-model="selectedRegion.name"
                        :placeholder="$t('shafts.group.input_text')"
                        type="text"
                        @input="validateInput('name')"
                      >
                    </div>
                  </div>
                </FormRow>

                <FormRow
                  v-if="hasType"
                  :errors="isValidField('region_type')"
                  :label="$t('regions.region_type_label')"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <SelectList
                      v-model="selectedRegionObject"
                      :class="{ 'input--error': isValidField('region_type') }"
                      :labelGroup="labelGroup"
                      :labelIcons="regionTypeLabelIcons"
                      :list="regionTypeOptions"
                      showIconsInList
                      @update:model-value="updateRegionType"
                    />
                  </div>
                </FormRow>

                <FormRow
                  :label="$t('regions.external_reference')"
                  labelFor="external_reference"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <div class="input">
                      <input
                        id="external_reference"
                        v-model="selectedRegion.external_reference"
                        :placeholder="$t('shafts.group.input_text')"
                        type="text"
                      >
                    </div>
                  </div>
                </FormRow>
              </div>
            </div>

            <div class="form form--separated">
              <div class="form-row">
                <button
                  class="button button--full-width typo-ignore typo-input"
                  type="button"
                  @click="deleteCurrentRegion"
                >
                  {{ $t('regions.delete_region') }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="layout-regions__map-wrapper">
          <HeaderBar :modifiers="['light', 'border-bottom']">
            <div class="radio-button radio-button--icon-only">
              <input
                id="tool_hand"
                v-model="tool"
                aria-label="Hand tool"
                class="radio-button__input"
                type="radio"
                value="hand"
              >
              <label
                class="radio-button__content"
                for="tool_hand"
              >
                <Hand32Svg class="radio-button__icon" />
              </label>
            </div>
            <div class="radio-button">
              <input
                id="tool_new_region"
                v-model="tool"
                class="radio-button__input"
                type="radio"
                value="new_region"
              >
              <label
                class="radio-button__content"
                for="tool_new_region"
              >
                <NewRegionSvg class="radio-button__icon" />
                <span class="radio-button__label">{{ $t('regions.new_region') }}</span>
              </label>
            </div>
            <template #right>
              <UndoRedo
                v-if="!loading"
                ref="undoRedo"
                :getState="getState"
                :map="map"
                @update="setState"
              />
            </template>
          </HeaderBar>
          <div class="layout-regions__map layout-regions__map--center show-for-medium">
            <LeafletMap
              v-if="!loading || !data?.has_pending_tile_process && data?.diagram_identifier"
              ref="map"
              :defaultPolygonOptions="regionPolygonOptions('site', false, false)"
              :diagram="data?.diagram_identifier"
              :editable="true"
              @init="onMapInit"
              @polygon-click="selectRegionOnMap"
              @polygon-complete="createPoly"
              @polygon-updated="updatePoly"
            />
            <EmptyMessage
              v-else-if="data?.has_pending_tile_process"
              :message="textData.pending_tile_process.description"
              :title="textData.pending_tile_process.title"
            />
            <EmptyMessage
              v-else-if="!data?.diagram_identifier"
              :message="textData.no_diagram.description"
              :title="textData.no_diagram.title"
            >
              <NoMobileAssetsSvg v-if="$route.params?.heavyMobileEquipmentId" />
              <StationSelectSvg v-if="$route.params?.stationId" />
            </EmptyMessage>
            <Loader
              v-else
              :modifiers="['overlay']"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, nextTick, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import { v4 as uuid } from 'uuid';
import useAjax from '@/functions/use_ajax';
import { useRouter } from 'vue-router';
import { rgeoPolygonToGeoJSON, geoJSONPolygonToLatLngs, latLngsToRgeo, latLngsToGeoJSON } from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import onEscapePressed from '@/functions/on_escape_pressed';
import { compareByAreaAndId } from '@/functions/compare_regions';
import booleanContains from '@turf/boolean-contains';
import { findAlteredRegion, filterAlteredRegion, clearError } from '@/functions/region';

// Components
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import AlertBar from '@/components/form/alert_bar.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import UndoRedo from '@/components/mapping/undo_redo.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import SelectList from '@/components/shaftsV2/operations/select_list.vue';

// Svg
import Hand32Svg from '@/images/hand-32.svg';
import NewRegionSvg from '@/images/new-region.svg';
import StationSelectSvg from '@/images/station-select.svg';
import NoMobileAssetsSvg from '@/images/no-mobile-assets.svg';
import ExclamationOctagonSvg from '@/images/exclamation_octogon.svg';

const props = defineProps({
  /** The type of form in progress */
  isHme: { type: Boolean, required: false, default: false },
  /** Toggles the type field */
  hasType: { type: Boolean, required: false, default: false },
  /** The regions endpoint */
  regionsEndPoint: { type: String, required: true, default: null },
  /** Hash with validation keys */
  validationData: { type: Object, required: true, default: null },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
  /** The current form data, new or existing */
  data: { type: Object, required: true, default: null },
});

const router = useRouter();
const ajax = useAjax();

const loading = ref(true);
const regions = ref(null);
const unsortedRegions = ref(null);
const regionMap = ref(null);
const regionTree = ref([]);
const polygonMap = new Map();
const selectedRegion = ref(null);
const tool = ref('hand');
const saving = ref(false);
const selectedRegionObject = ref(null);
const labelGroup = ref({ noItemLabel: I18n.t('shafts.group.no_type') });

const map = ref(null);
const undoRedo = ref(null);

const regionTypeLabelIcons = {};
const regionTypeOptions = computed(() => {
  if (!props.hasType) return;
  /**
   * prepares the element group list for the dropdown
   */
  const regionTypeList = Object.entries(I18n.t('shafts.element_types.station')).map(([value, label]) => ({ label, value }));
  regionTypeList.forEach(({ value }) => {
    regionTypeLabelIcons[value] = { name: 'circle_16', class: `icon icon--small region-type region-type--${value}` };
  });
  return regionTypeList;
});

const errorMessage = computed(() => null);

onEscapePressed((event) => {
  if (map.value.isDrawing()) {
    map.value.stopDrawing();
    tool.value = 'hand';
    event.preventDefault();
  }
});

watch(tool, () => {
  switch (tool.value) {
    case 'hand': {
      map.value.stopDrawing();
      break;
    }
    case 'new_region': {
      deselectRegion();
      map.value.startPoly();
      break;
    }
  }
});

watch(selectedRegionObject, () => {
  if (!props.hasType) return;
  // validate station region type
  validateInput('station', 'station_regions.region_type');
});

/**
 * Prepares the selectedRegionObject for the dropdown
 * and the selectedRegion for this page and post
 */
function setRegionType(regionType) {
  if (!props.hasType) return;
  if (typeof regionType === 'string') {
    return (selectedRegionObject.value = regionTypeOptions.value?.find((element) => element.value === regionType));
  } else if (typeof regionType === 'object') {
    return (selectedRegion.value.region_type = regionType?.value);
  }
}

function validateInput(field) {
  if (selectedRegion.value?.errors && selectedRegion.value.errors[props.validationData.operation]) {
    const fieldName = `${props.validationData.operation_regions}.${field}`;
    selectedRegion.value.errors[props.validationData.operation][fieldName] = null;
  }

  return [...unsortedRegions.value.values()].filter((r) => !r._destroy)?.forEach((region) => {
    // Remove validation for the selected region
    region.selected && (region.errors = null);
    updatePolygonStyle(region);
  });
}

function isValidField(field) {
  const hasError = selectedRegion.value?.errors && selectedRegion.value.errors[props.validationData.operation];
  const fieldName = `${props.validationData.operation_regions}.${field}`;
  return hasError && selectedRegion.value.errors[props.validationData.operation][fieldName];
}

function setValidationMessage(field, message) {
  const defaultMessage = [I18n.t('activerecord.errors.blank')];
  const fieldName = `${props.validationData.operation_regions}.${field}`;
  if (selectedRegion.value?.errors && selectedRegion.value.errors[props.validationData.operation]) {
    selectedRegion.value.errors[props.validationData.operation] = {...{ [fieldName]: message ? [message] : defaultMessage } };
  } else {
    selectedRegion.value['errors'] = {[props.validationData.operation]: { [fieldName]: message ? [message] : defaultMessage } };
  }
  updatePolygonStyle(selectedRegion.value);
}

function contains(region, other) {
  let p1 = polygonMap.get(region.id);
  let p2 = polygonMap.get(other.id);

  if (!p1.getBounds().contains(p2.getBounds())) return false;
  return booleanContains(region.geoJSON, other.geoJSON);
}

function updateRegionHierarchy() {
  let regions = [...regionMap.value.values()].filter((region) => !region._destroy);
  regions.forEach((region) => {
    region.parent = null;
    region.children = [];
  });
  regions.sort(compareByAreaAndId);
  regions.forEach((region) => {
    let x = regions.filter((other) => {
      // check that they don't contain each other (same poly)
      return (
        other.id !== region.id &&
        contains(region, other) &&
        !contains(other, region)
      );
    });
    x.forEach((other) => {
      other.parent = region;
      if (polygonMap.get(other.id)) {
        polygonMap.get(other.id).bringToFront();
      }
    });
  });
  regions.forEach((region) => {
    if (region.parent) region.parent.children.push(region);
  });

  regionTree.value = regions.filter((region) => !region.parent);
}

async function onMapInit() {
  regionMap.value = new Map();
  await loadRegions();

  regions.value?.forEach(loadRegion);

  if (regions.value?.length > 0) {
    selectRegion(regions.value[0]);
  }

  updateRegionHierarchy();
  loading.value = false;
}

async function loadRegions() {
  let { ok, data } = await ajax(props.regionsEndPoint);

  if (ok) {
    regions.value = data.regions;
    unsortedRegions.value = data.unsorted_regions;
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
  };
  loadPolygon(regionObject);

  regionMap.value.set(region.id, reactive(regionObject));
}

function loadPolygon(region) {
  let polygon = map.value.addPolygon(
    region.latLngs,
    regionPolygonOptions(region.region_type)
  );
  polygon.disableEdit();
  polygonMap.set(region.id, polygon);
  polygon.region = region;
}

function deselectRegion() {
  if (selectedRegion.value) {
    let previousRegion = regionMap.value.get(selectedRegion.value.id);
    previousRegion.selected = false;
    selectedRegion.value = null;
    selectedRegionObject.value = null;

    let previousPolygon = polygonMap.get(previousRegion.id);
    previousPolygon.disableEdit();
    updatePolygonStyle(previousRegion, previousPolygon);
  }
}

async function selectRegion(region, { goTo = true } = {}) {
  if (!region) return;

  deselectRegion();

  let newRegion = regionMap.value.get(region.id);
  let newPolygon = polygonMap.get(region.id);
  updatePolygonStyle(newRegion, newPolygon, true);
  newPolygon.enableEdit();
  selectedRegion.value = newRegion;
  setRegionType(selectedRegion.value.region_type);

  if (goTo) map.value.goToObject(newPolygon);

  await nextTick();
  newRegion.selected = true;
}

async function selectRegionOnMap(polygon) {
  await selectRegion(polygon.region);
}

function updateRegionType() {
  setRegionType(selectedRegionObject.value);
  let region = regionMap.value.get(selectedRegion.value.id);
  updatePolygonStyle(region);
}

function updatePoly(polygon, latLngs) {
  let region = polygon.region;
  region.bounds = latLngsToRgeo(latLngs);
  region.geoJSON = latLngsToGeoJSON(latLngs);
  region.latLngs = latLngs;
  setTimeout(updateRegionHierarchy, 1);
}

function createPoly(polygon, latLngs) {
  let region = {
    id: uuid(),
    name: '',
    isNew: true,
    latLngs: latLngs,
    bounds: latLngsToRgeo(latLngs),
    geoJSON: latLngsToGeoJSON(latLngs),
    selected: true,
  };
  polygon.region = region;

  regionMap.value.set(region.id, region);
  unsortedRegions.value.push(region);
  polygonMap.set(region.id, polygon);

  selectedRegion.value = region;
  selectedRegionObject.value = null;

  updateRegionHierarchy();
  tool.value = 'hand';

  undoRedo.value.pushUndo();
}

function deleteRegion(regionToDelete, pushUndo) {
  if (regionToDelete === selectedRegion.value) deselectRegion();
  let polygon = polygonMap.get(regionToDelete.id);
  if (polygon) {
    polygon.remove();
    polygonMap.delete(regionToDelete.id);
  }
  if (regionToDelete.isNew) {
    regionMap.value.delete(regionToDelete.id);
    const objWithIdIndex = unsortedRegions.value.findIndex((obj) => obj.id === regionToDelete.id);
    unsortedRegions.value.splice(objWithIdIndex, 1);
  } else {
    regionToDelete._destroy = true;
  }
  updateRegionHierarchy();
  if (pushUndo) undoRedo.value.pushUndo();
}

function deleteCurrentRegion() {
  deleteRegion(selectedRegion.value, true);
}

function formatRegionForPost(region) {
  let regionJson = {
    name: region.name,
    external_reference: region.external_reference,
    bounds: region.bounds,
  };
  // Add region type only where applicable
  props.hasType && (regionJson['region_type'] = region.region_type);

  if (!region.isNew) {
    if (region._destroy) {
      return { id: region.id, _destroy: true };
    }
    return {
      ...regionJson,
      id: region.id,
    };
  }

  return regionJson;
}

function formatRegionBodyForPost(alteredRegions) {
  const newRegionData = alteredRegions.map(formatRegionForPost);
  if (props.isHme) {
    return {
      heavy_mobile_equipment: {
        heavy_mobile_equipment_regions_attributes: newRegionData,
      },
    };
  }
  return {
    station: {
      station_regions_attributes: newRegionData,
    },
  };
}

function validateBounds(region) {
  let isValidBounds = true;
  const geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  geoJSON?.coordinates[0]?.forEach(coordinates => {
    if (map.value.diagramBoundsValidation(coordinates) && isValidBounds) {
      isValidBounds = !isValidBounds;
      return setValidationMessage('bounds', I18n.t('activerecord.errors.bounds'));
    }
  });
  return isValidBounds;
}

async function save() {
  // Validate region type only where applicable
  const isRegionTypeValid = props.hasType && selectedRegion.value?.name && !selectedRegion.value?.region_type;
  if (isRegionTypeValid) return setValidationMessage('region_type');

  if (selectedRegion.value && !validateBounds(selectedRegion.value)) return;

  saving.value = true;

  const fieldList = { main: ['bounds', 'external_reference', 'name', 'region_type'] };

  // Finds the altered region/regions and marks them with the altered key
  let alteredRegions = findAlteredRegion([...regionMap.value.values()], unsortedRegions.value, fieldList);

  // Clear region errors
  alteredRegions = clearError(alteredRegions);

  // Reset the region style
  selectedRegion.value && updatePolygonStyle(selectedRegion.value);
  updateRegionHierarchy();

  // Get list of altered, deleted and new regions
  alteredRegions = filterAlteredRegion(alteredRegions);

  if (alteredRegions.length <= 0) return router.back();

  let { ok, data } = await ajax(props.regionsEndPoint, {
    method: 'PATCH',
    body: formatRegionBodyForPost(alteredRegions),
  });

  if (ok) {
    router.back();
  } else {
    saving.value = false;
    postPatchError(data);
  }

  saving.value = false;
}

function postPatchError(data) {
  [...unsortedRegions.value.values()].filter((r) => !r._destroy).forEach((region, index) => {
    const errorObject = data[props.validationData.operation_regions][index]?.[props.validationData.operation_region];
    region.errors = errorObject;
    // If the selected region has no errors we select the one that has errors
    if (!selectedRegion.value?.errors && errorObject) {
      selectRegion(region);
      selectedRegion.value['errors'] = region.errors;
    }
    updatePolygonStyle(region);
  });

  // Add errors if there is a selected region
  selectedRegion.value && (selectedRegion.value['errors'] = data);
  updateRegionHierarchy();
}

function updatePolygonStyle(
  region,
  polygon = polygonMap.get(region?.id),
  selected = region?.selected
) {
  let style = regionPolygonOptions(region?.region_type, selected, region?.errors);
  polygon?.setStyle(style);
}

function getState() {
  let state = {};
  for (let region of regionMap.value.values()) {
    if (region._destroy) continue;
    state[region.id] = {
      id: region.id,
      name: region.name,
      bounds: region.bounds,
      isNew: region?.isNew,
    };
    // Add region type only where applicable
    props.hasType && (state[region.id]['region_type'] = region.region_type);
  }
  return state;
}

function setState(state) {
  for (let [id, value] of Object.entries(state)) {
    let region = regionMap.value.get(id);
    if (region) {
      updateRegionFromState(region, value);
    } else {
      loadRegion(value);
      unsortedRegions.value.push(value);
    }
  }
  for (let region of regionMap.value.values()) {
    if (!state[region.id]) {
      deleteRegion(region, false);
    }
  }

  updateRegionHierarchy();
}

function updateRegionFromState(region, state) {
  region.name = state.name;
  region.bounds = state.bounds;
  region.geoJSON = rgeoPolygonToGeoJSON(state.bounds);
  region.latLngs = geoJSONPolygonToLatLngs(region.geoJSON);
  if (region._destroy) region._destroy = false;
  let polygon = polygonMap.get(region.id);
  if (!polygon) {
    loadPolygon(region);
  } else {
    polygon.setLatLngs(region.latLngs);
    polygon.editor?.reset();
  }
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';
.current-item-name {
  margin: bu(1.8) bu(2);
}
</style>
